import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { callAPI, reloadToken } from '../../services/api.service';
import { Toast } from '../../components/Common/Toast';
import Loader from '../../components/Common/Loader';
import * as Yup from 'yup';
import BackButton from '../../components/Common/BackButton';
import 'react-phone-input-2/lib/style.css';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';
import { setRedirectURL, setUser } from '../../store/userSlice';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { mapToUserData } from '../Builder/components/SignUp/SignUp.mapper';
import { autoFetchUserDetails, forceFetchUserDetails } from '../../store/thunks/user.thunk';
import { TokenCookieManager } from '../../utils/tokenCookies';
import { getURLSearchParams } from '../../helper/helper';
import { resolveWpProjectIdFromProjectParam } from './services/wp-project.service';

// Create a simplified signup schema for email/password only
const SimpleSignUpSchema = () => {
  return Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    phoneNumber: Yup.string().optional(),
    password: Yup.string().required('Password is required').min(8, 'Must be at least 8 characters'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Please confirm your password'),
    confirm: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
    marketing: Yup.boolean(),
  });
};

const SignUp = () => {
  const dispatch: AppDispatch = useDispatch();
  const currentSearchQuery = useSelector((state: RootState) => state.app.currentMainURLQuery);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const userAuthorized = useSelector((state: RootState) => state.user.userAuthorized);
  const user = useSelector((state: RootState) => state.user.user);

  const searchParams = getURLSearchParams(true);
  const tallyEmail = searchParams.get('email') ?? '';
  const tallyProjectId = searchParams.get('project') ?? '';
  const tallyLeadId = searchParams.get('lead_id') ?? '';

  useEffect(() => {
    if (user && userAuthorized) {
      dispatch(setRedirectURL('/'));
    }
  }, [dispatch, user, userAuthorized]);

  const handleSignUpFormSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams(currentSearchQuery);

      const email = values.email.toLowerCase().trim();
      
      const resolvedProjectId = await resolveWpProjectIdFromProjectParam(tallyProjectId);

      const signUpData = {
        ...mapToUserData({
          signupViaGoogle: null,
          values,
          countryCode: queryParams.get('countryCode') || undefined,
          currency: queryParams.get('currency') || undefined,
        }),
        ...(resolvedProjectId ? { tallyProjectId: resolvedProjectId } : {}),
        ...(tallyLeadId ? { leadId: tallyLeadId } : {}),
      };

      const response = await callAPI('api/auth/teacher/signUp', signUpData);

      if (response?.status === 200 || response?.status === 201) {
        const firebaseResponse = await createUserWithEmailAndPassword(auth, email, values.password);
        await callAPI('api/update-firebase-id', {
          email: email,
          firebaseId: firebaseResponse?.user?.uid,
        });

        TokenCookieManager.removeToken();
        await reloadToken();

        dispatch(
          forceFetchUserDetails((user) => {
            if (!user) {
              return;
            }
            dispatch(setUser(user));
            dispatch(autoFetchUserDetails());
            Toast('Account created successfully!');
            dispatch(setRedirectURL('/'));
          }),
        );
      } else {
        Toast(response?.message || 'Failed to create account', true);
      }
    } catch (error: any) {
      Toast(error?.message || 'Failed to create account', true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sofiaSans justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm py-8 px-4 sm:px-10">
        <div className="text-left mb-6">
          <h1 className="text-3xl font-bold text-black mb-3">Teachers: foster a class that loves stories</h1>
          {/*<p className="text-black">Please use your school email address.</p>*/}
        </div>
        <div className="">
          <Formik
            initialValues={{
              firstName: '',
              lastName: '',
              email: tallyEmail,
              // phoneNumber: '',
              password: '',
              confirmPassword: '',
              confirm: false,
              marketing: false,
            }}
            validationSchema={SimpleSignUpSchema}
            onSubmit={(values) => {
              handleSignUpFormSubmit(values);
            }}
          >
            {({ errors, touched, values, handleChange, handleSubmit, setFieldValue }) => (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      placeholder="First Name"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    {touched.firstName && errors.firstName && (
                      <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.firstName}</div>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      placeholder="Last Name"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    {touched.lastName && errors.lastName && (
                      <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.lastName}</div>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    placeholder="School email (Recommended)"
                    className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                  />
                  {touched.email && errors.email && (
                    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.email}</div>
                  )}
                </div>

                {/*<div className="relative">*/}
                {/*  <PhoneInput*/}
                {/*    // country={'us'}*/}
                {/*    value={values.phoneNumber}*/}
                {/*    onChange={(phoneNumber) => setFieldValue('phoneNumber', phoneNumber)}*/}
                {/*    placeholder="Mobile Number (Optional)"*/}
                {/*    inputClass="!w-full !px-4 !pl-12 !py-2 !border !border-secondaryStoryGrayBorder !rounded-lg !text-base !text-secondaryStoryBlackText"*/}
                {/*    containerClass="!w-full"*/}
                {/*    buttonClass="!border !border-secondaryStoryGrayBorder !rounded-l-lg !bg-white hover:!bg-gray-50"*/}
                {/*    dropdownClass="!border !border-secondaryStoryGrayBorder !rounded-lg !shadow-lg"*/}
                {/*    // disableCountryCode={false}*/}
                {/*    // countryCodeEditable={false}*/}
                {/*    // disableCountryGuess={false}*/}
                {/*    preferredCountries={['us', 'ca', 'gb', 'au']}*/}
                {/*    inputStyle={{*/}
                {/*      width: '100%',*/}
                {/*      height: '42px',*/}
                {/*      fontSize: '16px',*/}
                {/*      paddingLeft: '48px',*/}
                {/*      border: '1px solid',*/}
                {/*      borderRadius: '8px',*/}
                {/*      outline: 'none',*/}
                {/*    }}*/}
                {/*    containerStyle={{*/}
                {/*      width: '100%',*/}
                {/*    }}*/}
                {/*    buttonStyle={{*/}
                {/*      border: '1px solid',*/}
                {/*      borderRadius: '8px 0 0 8px',*/}
                {/*      backgroundColor: 'white',*/}
                {/*    }}*/}
                {/*  />*/}
                {/*  {touched.phoneNumber && errors.phoneNumber && (*/}
                {/*    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.phoneNumber}</div>*/}
                {/*  )}*/}
                {/*</div>*/}

                <div className="relative">
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      placeholder="Password"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEye : faEyeSlash}
                        className="text-gray-400 hover:text-gray-600"
                      />
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.password}</div>
                  )}
                </div>

                <div className="relative">
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm Password"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <FontAwesomeIcon
                        icon={showConfirmPassword ? faEye : faEyeSlash}
                        className="text-gray-400 hover:text-gray-600"
                      />
                    </button>
                  </div>
                  {touched.confirmPassword && errors.confirmPassword && (
                    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.confirmPassword}</div>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex text-left">
                    <input
                      id="confirm"
                      name="confirm"
                      type="checkbox"
                      checked={values.confirm}
                      onChange={(e) => setFieldValue('confirm', e.target.checked)}
                      className={`h-4 w-4 mt-0.5 focus:ring-secondaryStoryAdditionalLightBlueText rounded border-none text-secondaryStoryAdditionalLightBlueText`}
                    />
                    <label
                      htmlFor="confirm"
                      className={`ml-2 block text-sm ${touched.confirm && errors.confirm ? 'text-storyseconderror' : 'text-secondaryDescriptionTextColor'} `}
                    >
                      I’m a licensed teacher using this with my class and I give permission for them to use Build My
                      Story. I agree to the{' '}
                      <Link
                        to="https://buildmystory.com/terms-and-conditions/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondaryStoryAdditionalLightBlueText hover:underline"
                      >
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="https://buildmystory.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondaryStoryAdditionalLightBlueText hover:underline"
                      >
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  <div className="flex text-left">
                    <input
                      id="marketing"
                      name="marketing"
                      type="checkbox"
                      checked={values.marketing}
                      onChange={(e) => setFieldValue('marketing', e.target.checked)}
                      className="h-4 w-4 mt-0.5 text-secondaryStoryAdditionalLightBlueText focus:ring-secondaryStoryAdditionalLightBlueText border-secondaryStoryGrayBorder rounded"
                    />
                    <label htmlFor="marketing" className="ml-2 block text-sm text-secondaryDescriptionTextColor">
                      Yes, I’d like helpful updates and free classroom tools.
                    </label>
                  </div>
                  {/*<div className="flex items-center mt-2">*/}
                  {/*  <div className="block text-sm text-secondaryStoryBlackGrayText">*/}
                  {/*    By singing up, you accept our{' '}*/}
                  {/*    <Link*/}
                  {/*      to={'https://buildmystory.com/terms-and-conditions/'}*/}
                  {/*      target="_blank"*/}
                  {/*      rel="noopener noreferrer"*/}
                  {/*      className="text-secondaryStoryAdditionalLightBlueText cursor-pointer hover:underline"*/}
                  {/*    >*/}
                  {/*      Terms*/}
                  {/*    </Link>{' '}*/}
                  {/*    and{' '}*/}
                  {/*    <Link*/}
                  {/*      to={'https://buildmystory.com/privacy-policy/'}*/}
                  {/*      target="_blank"*/}
                  {/*      rel="noopener noreferrer"*/}
                  {/*      className="text-secondaryStoryAdditionalLightBlueText cursor-pointer hover:underline"*/}
                  {/*    >*/}
                  {/*      Privacy*/}
                  {/*    </Link>*/}
                  {/*  </div>*/}
                  {/*</div>*/}
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-base font-bold text-white bg-secondaryStoryAdditionalLightBlueText hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sign Up
                    <Loader loader={isLoading} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <BackButton onClick={() => window.history.back()}></BackButton>
                  </div>
                  <Link
                    to="/auth/teacher"
                    className="text-secondaryStoryAdditionalLightBlueText hover:underline text-base font-bold"
                  >
                    Log In
                  </Link>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
